<template>
  <modal-view
    :title="`Transfer Request #${
      request?.local_transfer_request_id || request?.id || ''
    }`"
    :custom-max-width="'1000px'"
    :custom-content-height="'85vh'"
    custom-class="catalog-transfer-request-modal"
    custom-class-content="catalog-transfer-request-modal__content"
    @close-modal="$emit('close')"
  >
    <div class="transfer-request-modern flex h-full flex-col">
      <!-- Header Info -->
      <div
        class="transfer-request-hero border-b border-gray-200 px-6 py-4 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 space-y-3">
            <!-- Main Info Row -->
            <div class="flex items-start gap-6">
              <!-- From/To Column -->
              <div class="text-sm space-y-1.5 min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >From:</span
                  >
                  <span class="text-gray-900 dark:text-white truncate">{{
                    storeName(request?.store_id)
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >To:</span
                  >
                  <span class="text-gray-900 dark:text-white truncate">{{
                    storeName(request?.other_store_id)
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >Status:</span
                  >
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      getStatusClass(request?.status),
                    ]"
                  >
                    {{ request?.status }}
                  </span>
                </div>
              </div>

              <!-- Timeline Column -->
              <div class="text-sm space-y-1.5 min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <i class="fas fa-calendar-plus text-gray-400 w-4"></i>
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >Created:</span
                  >
                  <span class="text-gray-900 dark:text-white text-xs">{{
                    formatDateTime(request?.created_at)
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="fas fa-clock text-gray-400 w-4"></i>
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >Updated:</span
                  >
                  <span class="text-gray-900 dark:text-white text-xs">{{
                    formatDateTime(request?.updated_at)
                  }}</span>
                </div>
                <div
                  v-if="request?.transfer_id"
                  class="flex items-center gap-2"
                >
                  <i class="fas fa-exchange-alt text-gray-400 w-4"></i>
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >Transfer:</span
                  >
                  <span class="text-gray-900 dark:text-white text-xs"
                    >#{{ request?.transfer_id }}</span
                  >
                </div>
              </div>
            </div>

            <!-- Note Section -->
            <div
              v-if="request?.note"
              class="pt-2 border-t border-gray-200 dark:border-gray-700"
            >
              <div class="flex items-start gap-2">
                <i class="fas fa-sticky-note text-amber-500 mt-0.5"></i>
                <div class="flex-1 min-w-0">
                  <span
                    class="font-semibold text-gray-700 dark:text-gray-300 text-sm"
                    >Note:</span
                  >
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    {{ request?.note }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center gap-2">
            <button
              v-if="request?.status === 'PENDING' && !allItemsLinked"
              @click="showStartConfirmation = true"
              class="inline-flex items-center px-3 py-1.5 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <i class="fas fa-play mr-1.5"></i>
              Start Fulfillment
            </button>
            <button
              v-if="
                (request?.status === 'PENDING' ||
                  request?.status === 'IN_FULFILLMENT') &&
                allItemsLinked
              "
              @click="showCompleteConfirmation = true"
              class="inline-flex items-center px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <i class="fas fa-check mr-1.5"></i>
              Complete Transfer
            </button>
            <button
              v-if="['PENDING', 'IN_FULFILLMENT'].includes(request?.status)"
              @click="showRejectConfirmation = true"
              class="inline-flex items-center px-3 py-1.5 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50"
            >
              <i class="fas fa-times mr-1.5"></i>
              Reject
            </button>
          </div>
        </div>
      </div>

      <!-- Items List -->
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div class="space-y-3">
          <div
            v-for="item in items"
            :key="item.id"
            class="transfer-request-line-card rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900 overflow-hidden"
          >
            <!-- Header with status and quantity -->
            <div
              class="bg-gray-50 dark:bg-gray-800/50 px-4 py-2.5 flex items-center justify-between border-b border-gray-200 dark:border-gray-700"
            >
              <div class="flex items-center gap-2">
                <span
                  v-if="!item.product_mapping_id"
                  class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                >
                  <i class="fas fa-unlink mr-1.5"></i>
                  Not Linked
                </span>
                <span
                  v-else
                  class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200"
                >
                  <i class="fas fa-link mr-1.5"></i>
                  Linked
                </span>
                <button
                  v-if="
                    item.product_mapping_id &&
                    ['PENDING', 'IN_FULFILLMENT'].includes(request?.status)
                  "
                  @click="confirmRemoveLink(item)"
                  class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ml-1"
                  title="Remove link"
                >
                  <i class="fas fa-unlink text-sm"></i>
                </button>
              </div>
              <span
                class="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-sm"
              >
                <i class="fas fa-arrow-right mr-2"></i>
                {{ quantityLabel(item) }}
              </span>
            </div>

            <!-- 50/50 Product Display -->
            <div class="transfer-request-product-grid grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
              <!-- LEFT: Source Product (Requester) -->
              <div class="p-4">
                <div class="flex items-center gap-2 mb-2">
                  <i
                    class="fas fa-hand-holding text-amber-600 dark:text-amber-400"
                  ></i>
                  <span
                    class="text-xs font-bold uppercase text-gray-700 dark:text-gray-300"
                  >
                    Requested By
                  </span>
                </div>
                <h4
                  class="text-sm font-semibold text-gray-900 dark:text-white mb-2"
                >
                  {{ item.product?.name || `Product #${item.product_id}` }}
                </h4>
                <div class="space-y-1.5 text-xs">
                  <div
                    class="flex items-center gap-2 text-gray-600 dark:text-gray-400"
                  >
                    <span class="font-medium w-16">SKU:</span>
                    <span class="font-mono">{{
                      item.product?.item_sku || "N/A"
                    }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span
                      class="font-medium w-16 text-gray-600 dark:text-gray-400"
                      >Store:</span
                    >
                    <span
                      class="font-medium text-amber-700 dark:text-amber-300"
                    >
                      {{ storeName(request?.store_id) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- RIGHT: Target Product (Fulfiller) -->
              <div class="p-4 bg-blue-50/30 dark:bg-blue-900/5">
                <div class="flex items-center gap-2 mb-2">
                  <i
                    class="fas fa-warehouse text-blue-600 dark:text-blue-400"
                  ></i>
                  <span
                    class="text-xs font-bold uppercase text-gray-700 dark:text-gray-300"
                  >
                    Fulfilled By
                  </span>
                </div>

                <div v-if="item.product_mapping_id && item.other_product">
                  <h4
                    class="text-sm font-semibold text-gray-900 dark:text-white mb-2"
                  >
                    {{ item.other_product.name }}
                  </h4>
                  <div class="space-y-1.5 text-xs">
                    <div
                      class="flex items-center gap-2 text-gray-600 dark:text-gray-400"
                    >
                      <span class="font-medium w-16">SKU:</span>
                      <span class="font-mono">
                        {{
                          item.other_product.item_sku ||
                          item.other_product.sku ||
                          "N/A"
                        }}
                      </span>
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="font-medium w-16 text-gray-600 dark:text-gray-400"
                        >Store:</span
                      >
                      <span
                        class="font-medium text-blue-700 dark:text-blue-300"
                      >
                        {{ storeName(request?.other_store_id) }}
                      </span>
                    </div>

                    <!-- Inventory Info -->
                    <div
                      v-if="targetInventoryMap.has(item.other_product.id)"
                      class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700"
                    >
                      <div class="space-y-2">
                        <div
                          :class="[
                            'inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold',
                            packagingTransferSummary(item).remainingBase >= 0
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                          ]"
                        >
                          <i class="fas fa-boxes mr-1.5"></i>
                          {{
                            targetInventoryMap.get(item.other_product.id)
                              .quantity
                          }}
                          {{
                            targetInventoryMap.get(item.other_product.id)
                              .quantity_unit
                          }}
                          in stock
                        </div>

                        <div class="text-xs text-gray-600 dark:text-gray-400">
                          <i class="fas fa-info-circle mr-1 text-blue-500"></i>
                          Left after transfer:
                          <span
                            :class="[
                              'font-semibold ml-1',
                              packagingTransferSummary(item).remainingBase >= 0
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-red-700 dark:text-red-300',
                            ]"
                          >
                            {{ packagingTransferSummary(item).remaining }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="text-xs text-gray-500 dark:text-gray-400 italic"
                >
                  <i class="fas fa-unlink mr-1"></i>
                  Not mapped to a product yet
                </div>
              </div>
            </div>

            <!-- Unit conversion guidance -->
            <div
              v-if="item.unit_conversion"
              class="border-t border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900 dark:bg-blue-900/20"
            >
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div class="flex min-w-0 flex-1 items-start gap-2">
                  <i class="fas fa-scale-balanced mt-0.5 text-blue-600 dark:text-blue-300"></i>
                  <div class="min-w-0">
                    <p class="text-xs font-semibold text-blue-900 dark:text-blue-100">
                      {{ item.unit_conversion.label }}
                    </p>
                    <p class="mt-0.5 text-xs text-blue-700 dark:text-blue-300">
                      {{ storeName(request?.other_store_id) }} will dispatch
                      {{ requiredOriginQuantity(item).toFixed(2) }}
                      {{ requiredOriginUnit(item) }}; {{ storeName(request?.store_id) }}
                      receives {{ item.unit_conversion.target_quantity }}
                      {{ item.unit_conversion.target_quantity_unit }}.
                    </p>
                    <p
                      v-if="requiresExplicitConversion(item)"
                      class="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300"
                    >
                      Base units differ across the mapped products. This uses the
                      current mapping factor; review the product link if that is
                      not intended.
                    </p>
                  </div>
                </div>
                <button
                  v-if="item.unit_conversion.default_quantity_unit_update_option"
                  type="button"
                  :disabled="applyingUnitOption[item.id]"
                  @click="applyDefaultQuantityUnitOption(item)"
                  class="inline-flex items-center rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-200 dark:hover:bg-blue-900"
                >
                  <i
                    :class="[
                      applyingUnitOption[item.id]
                        ? 'fas fa-spinner fa-spin'
                        : 'fas fa-wand-magic-sparkles',
                      'mr-1.5',
                    ]"
                  ></i>
                  {{ item.unit_conversion.default_quantity_unit_update_option.label }}
                </button>
              </div>
            </div>

            <!-- Warning banner for insufficient inventory -->
            <div
              v-if="
                item.other_product?.id &&
                targetInventoryMap.has(item.other_product.id) &&
                packagingTransferSummary(item).remainingBase < 0
              "
              class="border-t border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20"
            >
              <div class="flex items-start gap-2">
                <i
                  class="fas fa-exclamation-circle text-red-600 dark:text-red-400 mt-0.5"
                ></i>
                <div class="flex-1">
                  <p
                    class="text-xs font-semibold text-red-900 dark:text-red-200"
                  >
                    ⚠️ Insufficient Inventory
                  </p>
                  <p class="text-xs text-red-700 dark:text-red-300 mt-0.5">
                    {{ storeName(request?.other_store_id) }} only has
                    {{ targetInventoryMap.get(item.other_product.id).quantity }}
                    {{
                      targetInventoryMap.get(item.other_product.id)
                        .quantity_unit
                    }}
                    but {{ item.quantity }} {{ item.quantity_unit }} is
                    requested. Shortfall:
                    {{ packagingTransferSummary(item).shortfall }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Inline Mapping Creation -->
            <div
              v-if="
                !item.product_mapping_id &&
                ['PENDING', 'IN_FULFILLMENT'].includes(request?.status) &&
                !item.creatingMapping
              "
              class="border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50"
            >
              <button
                @click="startCreatingMapping(item)"
                class="inline-flex items-center rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <i class="fas fa-plus mr-1.5"></i>
                Link Product Now
              </button>
            </div>

            <!-- Inline Search and Link -->
            <div
              v-if="item.creatingMapping"
              class="border-t border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20"
            >
              <div class="space-y-2">
                <label
                  class="block text-xs font-semibold text-blue-900 dark:text-blue-200"
                >
                  Select target product in
                  {{ storeName(request?.other_store_id) }}
                </label>
                <div class="relative">
                  <input
                    v-model="item.targetSearchQuery"
                    @input="searchTargetProducts(item)"
                    type="text"
                    placeholder="Search by name or SKU..."
                    class="w-full rounded-md border border-blue-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-blue-700 dark:bg-gray-900 dark:text-white"
                  />
                  <i
                    v-if="item.searchingTargetProducts"
                    class="fas fa-spinner fa-spin absolute right-3 top-2.5 text-gray-400"
                  ></i>
                </div>

                <!-- Search Results -->
                <div
                  v-if="
                    item.targetProductResults &&
                    item.targetProductResults.length > 0
                  "
                  class="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <button
                    v-for="product in item.targetProductResults"
                    :key="product.id"
                    @click="selectTargetProduct(item, product)"
                    class="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div class="font-medium text-gray-900 dark:text-white">
                      {{ product.name }}
                    </div>
                    <div
                      class="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400"
                    >
                      <span
                        >SKU:
                        {{ product.item_sku || product.sku || "N/A" }}</span
                      >
                      <span
                        v-if="
                          product.quantity_unit || product.default_quantity_unit
                        "
                        class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium dark:bg-gray-700"
                      >
                        Unit:
                        {{
                          product.quantity_unit || product.default_quantity_unit
                        }}
                      </span>
                    </div>
                  </button>
                </div>

                <div
                  v-else-if="
                    item.targetSearchQuery && !item.searchingTargetProducts
                  "
                  class="text-xs text-gray-500 dark:text-gray-400 py-2"
                >
                  No products found. Try a different search.
                </div>

                <div class="flex items-center gap-2 pt-1">
                  <button
                    @click="cancelCreatingMapping(item)"
                    class="text-xs text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <hs-modal
      v-model="showLinkConfirmation"
      title="Confirm Product Link"
      size="md"
      :zIndex="5600"
      :panel-style="{ width: 'min(100%, 700px)' }"
    >
      <div class="space-y-6 p-2 sm:p-4">
        <div class="flex items-start gap-4">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30"
          >
            <i class="fas fa-link text-2xl text-blue-600 dark:text-blue-400"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Source Product
            </p>
            <p class="text-base font-semibold text-gray-900 dark:text-white">
              {{ pendingLink?.sourceItem?.product?.name }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              SKU: {{ pendingLink?.sourceItem?.product?.item_sku }}
            </p>
          </div>
        </div>

        <div class="flex items-center justify-center text-gray-400 dark:text-gray-500">
          <i class="fas fa-arrow-down"></i>
        </div>

        <div class="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
          <p class="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">
            Target Product
          </p>
          <p class="text-base font-semibold text-gray-900 dark:text-white">
            {{ pendingLink?.targetProduct?.name }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            SKU: {{ pendingLink?.targetProduct?.item_sku || pendingLink?.targetProduct?.sku }}
          </p>
        </div>

        <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
          <i class="fas fa-info-circle mr-2 text-amber-500"></i>
          This will create a permanent link between these products for inventory transfers.
        </div>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showLinkConfirmation = false">Cancel</hs-button>
        <hs-button variant="primary" @click="confirmLinkProducts">Yes, Link Products</hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showRemoveLinkConfirmation"
      title="Remove Product Link"
      size="sm"
      :zIndex="5600"
      :panel-style="{ width: 'min(100%, 600px)' }"
    >
      <div class="space-y-6 p-2 sm:p-4">
        <div class="flex items-start gap-4">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30"
          >
            <i class="fas fa-unlink text-2xl text-red-600 dark:text-red-400"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Remove Product Link
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Are you sure you want to remove the link for this item?
            </p>
            <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ itemToUnlink?.product?.name }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                SKU: {{ itemToUnlink?.product?.item_sku }}
              </p>
            </div>
            <p class="mt-3 text-xs text-amber-700 dark:text-amber-300">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              You can re-link this product later if needed.
            </p>
          </div>
        </div>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showRemoveLinkConfirmation = false">Cancel</hs-button>
        <hs-button variant="danger" @click="confirmRemoveLinkAction">Yes, Remove Link</hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showStartConfirmation"
      title="Start Fulfillment"
      size="md"
      :zIndex="5600"
      :panel-style="{ width: 'min(100%, 700px)' }"
    >
      <div class="space-y-6 p-2 sm:p-4">
        <div class="flex items-start gap-4">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30"
          >
            <i class="fas fa-play text-2xl text-amber-600 dark:text-amber-400"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Start Fulfillment</p>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to start fulfilling this transfer request?
            </p>
            <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
              <p class="text-xs text-gray-700 dark:text-gray-300">
                <i class="fas fa-info-circle mr-1 text-blue-500"></i>
                The request status will change to <strong>IN_FULFILLMENT</strong>. You can link products and complete the transfer after starting.
              </p>
            </div>
            <div class="mt-4">
              <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Add Note (Optional)
              </label>
              <textarea
                v-model="startNote"
                rows="2"
                placeholder="e.g., Started fulfillment for urgent order..."
                class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showStartConfirmation = false">Cancel</hs-button>
        <hs-button variant="primary" @click="handleStart">Yes, Start Fulfillment</hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showCompleteConfirmation"
      title="Complete Transfer Request"
      size="xl"
      :zIndex="5600"
      :panel-style="{ width: 'min(100%, 920px)' }"
    >
      <div class="space-y-6 p-2 sm:p-4">
        <div class="flex items-start gap-4">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30"
          >
            <i class="fas fa-exclamation-triangle text-2xl text-amber-600 dark:text-amber-400"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Complete Transfer Request
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">
              You are about to complete this transfer request and execute the inventory transfer.
            </p>
            <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Items to Transfer ({{ items.length }})
              </h4>
              <div class="space-y-2 max-h-60 overflow-y-auto">
                <div
                  v-for="item in items"
                  :key="item.id"
                  class="grid gap-3 rounded border border-gray-100 bg-white p-3 text-xs dark:border-gray-700 dark:bg-gray-800 sm:grid-cols-[minmax(0,1fr)_220px]"
                >
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 dark:text-white truncate">
                      {{ item.product?.name }}
                    </div>
                    <div class="text-gray-500 dark:text-gray-400">
                      {{ item.quantity }} {{ item.quantity_unit }}
                    </div>
                    <div v-if="item.unit_conversion" class="mt-0.5 text-blue-600 dark:text-blue-300">
                      {{ item.unit_conversion.label }}
                    </div>
                    <div
                      class="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-[11px] text-blue-900 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-100"
                    >
                      <div class="mb-2 flex items-center gap-1.5 font-bold uppercase tracking-wide">
                        <i class="fas fa-box-open text-blue-500"></i>
                        Packaging flow
                      </div>
                      <div class="grid gap-1.5 sm:grid-cols-2">
                        <div>
                          <span class="font-semibold">Requested:</span>
                          {{ packagingTransferSummary(item).requested }}
                        </div>
                        <div>
                          <span class="font-semibold">Available:</span>
                          {{ packagingTransferSummary(item).available }}
                        </div>
                        <div>
                          <span class="font-semibold">Sending:</span>
                          {{ packagingTransferSummary(item).sending }}
                        </div>
                        <div>
                          <span class="font-semibold">Remaining:</span>
                          <span
                            :class="
                              packagingTransferSummary(item).remainingBase >= 0
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-red-700 dark:text-red-300'
                            "
                          >
                            {{ packagingTransferSummary(item).remaining }}
                          </span>
                        </div>
                      </div>
                      <p
                        v-if="packagingTransferSummary(item).usesPackaging"
                        class="mt-2 text-blue-700 dark:text-blue-300"
                      >
                        {{ packagingTransferSummary(item).formula }}
                      </p>
                    </div>
                  </div>
                  <div class="min-w-0">
                    <label class="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Purchase unit price
                    </label>
                    <div class="flex min-h-[38px] items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 focus-within:border-green-500 focus-within:ring-2 focus-within:ring-green-500/20 dark:border-gray-700 dark:bg-gray-900">
                      <i class="fas fa-tag text-gray-400"></i>
                      <input
                        v-model.number="purchaseUnitPrices[item.id]"
                        type="number"
                        min="0"
                        step="0.01"
                        class="min-w-0 flex-1 bg-transparent py-2 text-sm font-semibold text-gray-900 outline-none dark:text-white"
                        placeholder="0"
                      />
                      <span class="flex-shrink-0 text-[11px] font-semibold text-gray-500">
                        /{{ item.quantity_unit || "unit" }}
                      </span>
                    </div>
                    <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {{ purchasePriceLoading[item.id] ? "Loading current purchase price..." : (purchasePriceHints[item.id] || "Default from fulfiller purchase") }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p class="text-xs font-medium text-red-900 dark:text-red-200">
                <i class="fas fa-exclamation-circle mr-1"></i>
                This action cannot be undone
              </p>
              <p class="mt-1 text-xs text-red-700 dark:text-red-300">
                Inventory will be removed from {{ storeName(request?.other_store_id) }} (Fulfiller) and sent to {{ storeName(request?.store_id) }} (Requester). This transfer cannot be reversed automatically.
              </p>
            </div>
            <div class="mt-4">
              <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Add Note (Optional)
              </label>
              <textarea
                v-model="completeNote"
                rows="2"
                placeholder="e.g., Transfer completed successfully..."
                class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showCompleteConfirmation = false">Cancel</hs-button>
        <hs-button variant="danger" :disabled="!canCompleteWithPrices" @click="handleComplete">Yes, Complete Transfer</hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showRejectConfirmation"
      title="Reject Transfer Request"
      size="md"
      :zIndex="5600"
      :panel-style="{ width: 'min(100%, 700px)' }"
    >
      <div class="space-y-6 p-2 sm:p-4">
        <div class="flex items-start gap-4">
          <div
            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30"
          >
            <i class="fas fa-times-circle text-2xl text-red-600 dark:text-red-400"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Reject Transfer Request
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to reject this transfer request?
            </p>
            <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
              <p class="text-xs text-gray-700 dark:text-gray-300">
                The requesting store will be notified that this request was rejected.
              </p>
            </div>
            <div class="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
              <h4 class="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                Requested items and packaging
              </h4>
              <div class="max-h-56 space-y-2 overflow-y-auto">
                <div
                  v-for="item in items"
                  :key="item.id"
                  class="rounded border border-gray-100 bg-white p-3 text-xs dark:border-gray-700 dark:bg-gray-800"
                >
                  <div class="font-medium text-gray-900 dark:text-white">
                    {{ item.product?.name || `Product #${item.product_id}` }}
                  </div>
                  <div class="mt-2 grid gap-1.5 text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                    <div>
                      <span class="font-semibold">Requested:</span>
                      {{ packagingTransferSummary(item).requested }}
                    </div>
                    <div>
                      <span class="font-semibold">Available:</span>
                      {{ packagingTransferSummary(item).available }}
                    </div>
                    <div>
                      <span class="font-semibold">Would send:</span>
                      {{ packagingTransferSummary(item).sending }}
                    </div>
                    <div>
                      <span class="font-semibold">Would remain:</span>
                      {{ packagingTransferSummary(item).remaining }}
                    </div>
                  </div>
                  <p
                    v-if="packagingTransferSummary(item).usesPackaging"
                    class="mt-2 text-blue-600 dark:text-blue-300"
                  >
                    {{ packagingTransferSummary(item).formula }}
                  </p>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Rejection Reason (Optional)
              </label>
              <textarea
                v-model="rejectNote"
                rows="2"
                placeholder="e.g., Insufficient stock, unable to fulfill at this time..."
                class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showRejectConfirmation = false">Cancel</hs-button>
        <hs-button variant="danger" @click="handleReject">Yes, Reject Request</hs-button>
      </template>
    </hs-modal>
  </modal-view>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from "vue";
import ModalView from "@/components/modals/ModalView.vue";
import { HsModal, HsButton } from "@/components/ui";
import http from "@/helper/http";
import { toast } from "vue3-toastify";

export default {
  name: "TransferRequestDetailsModal",
  components: { ModalView, HsModal, HsButton },
  props: {
    isOpen: { type: Boolean, default: false },
    groupId: { type: [String, Number], required: true },
    request: { type: Object, default: null },
    storeName: { type: Function, required: true },
  },
  emits: ["close", "start", "complete", "reject"],
  setup(props, { emit }) {
    const items = ref([]);
    const targetInventoryMap = ref(new Map()); // Map of product_id -> inventory quantity (TARGET store has inventory)
    const targetQuantityUnitsMap = ref(new Map());
    const showStartConfirmation = ref(false);
    const showCompleteConfirmation = ref(false);
    const showRejectConfirmation = ref(false);
    const showLinkConfirmation = ref(false);
    const showRemoveLinkConfirmation = ref(false);
    const pendingLink = ref(null);
    const itemToUnlink = ref(null);
    const startNote = ref("");
    const completeNote = ref("");
    const rejectNote = ref("");
    const applyingUnitOption = ref({});
    const purchaseUnitPrices = ref({});
    const purchasePriceHints = ref({});
    const purchasePriceLoading = ref({});

    const allItemsLinked = computed(() => {
      return (items.value || []).every((i) => !!i.product_mapping_id);
    });

    const canCompleteWithPrices = computed(() => {
      return (items.value || []).every(
        (item) => Number(purchaseUnitPrices.value[item.id] ?? 0) >= 0
      );
    });

    const getStatusClass = (status) => {
      const classes = {
        PENDING:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        IN_FULFILLMENT:
          "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        COMPLETED:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        REJECTED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
        CANCELED:
          "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
      };
      return classes[status] || classes.PENDING;
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        // Show relative time for recent dates
        if (diffMins < 1) return "Just now";
        if (diffMins < 60)
          return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
        if (diffHours < 24)
          return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
        if (diffDays < 7)
          return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

        // Show full date for older dates using global utility
        const { proxy } = getCurrentInstance();
        return proxy.$formatDateTime(dateString);
      } catch {
        return String(dateString);
      }
    };

    const formatMoney = (value) => {
      const numberValue = Number(value || 0);
      if (!Number.isFinite(numberValue)) return "RWF 0";
      return `RWF ${numberValue.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    };

    const unitCode = (value) => String(value || "U").trim().toUpperCase();

    const formatQuantity = (value) => {
      const numberValue = Number(value || 0);
      if (!Number.isFinite(numberValue)) return "0";
      return numberValue.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    };

    const packageFactorFromUnit = (unit) => {
      const code = unitCode(unit);
      const match = code.match(/-(\d+(?:\.\d+)?)$/);
      if (!match) return 1;
      const factor = Number(match[1]);
      return Number.isFinite(factor) && factor > 0 ? factor : 1;
    };

    const normalizeUnitRecord = (unit, fallbackProductId = null) => {
      const code = unitCode(unit?.unit || unit?.code || unit?.unit_code);
      const factor = Number(
        unit?.conversion_factor_to_base || unit?.factor || packageFactorFromUnit(code)
      );
      const baseUnit = unitCode(
        unit?.base_unit || unit?.parent_unit || (factor > 1 ? "U" : code)
      );
      const label = String(
        unit?.display_label || unit?.label || unit?.name || code
      ).trim();
      return {
        code,
        label: label && label.toUpperCase() !== code ? label : code,
        scope: unit?.scope || "system",
        factor: Number.isFinite(factor) && factor > 0 ? factor : 1,
        baseUnit,
        productId: unit?.product_id || fallbackProductId,
      };
    };

    const unitInfoForProduct = (productId, code) => {
      const normalizedCode = unitCode(code);
      const units = targetQuantityUnitsMap.value.get(productId) || [];
      return (
        units.find((unit) => unit.code === normalizedCode) ||
        normalizeUnitRecord({ unit: normalizedCode }, productId)
      );
    };

    const conversionFactorForItem = (item, inventoryUnit) => {
      const source = item?.unit_conversion?.source || {};
      const conversion = item?.unit_conversion?.product_mapping_unit_conversion || {};
      const inventoryUnitInfo = item?.other_product?.id
        ? unitInfoForProduct(item.other_product.id, inventoryUnit)
        : normalizeUnitRecord({ unit: inventoryUnit });
      const candidates = [
        inventoryUnitInfo.factor,
        source.conversion_factor_to_base,
        source.factor,
        source.quantity_unit_factor,
        source.quantity_unit_conversion_factor,
        conversion.source_conversion_factor_to_base,
        conversion.conversion_factor_to_base,
        conversion.factor,
        packageFactorFromUnit(inventoryUnit),
      ];
      const factor = candidates
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value > 0);
      return factor || 1;
    };

    const requiredOriginQuantity = (item) => {
      const quantity = item?.unit_conversion?.source?.quantity_in_base_unit;
      return Number(quantity ?? item?.quantity ?? 0);
    };

    const requiredOriginUnit = (item) => {
      return (
        item?.unit_conversion?.source?.base_quantity_unit ||
        item?.unit_conversion?.target_quantity_unit ||
        item?.quantity_unit ||
        item?.product?.quantity_unit ||
        "U"
      );
    };

    const packagingTransferSummary = (item) => {
      const inventory = item?.other_product?.id
        ? targetInventoryMap.value.get(item.other_product.id)
        : null;
      const inventoryQuantity = Number(inventory?.quantity ?? 0);
      const productId = item?.other_product?.id;
      const inventoryUnit = unitCode(
        inventory?.price_unit_code ||
          inventory?.quantity_unit ||
          item?.other_product?.quantity_unit ||
          item?.unit_conversion?.source?.quantity_unit ||
          item?.quantity_unit ||
          "U"
      );
      const inventoryUnitInfo = productId
        ? unitInfoForProduct(productId, inventoryUnit)
        : normalizeUnitRecord({ unit: inventoryUnit });
      const baseUnit = unitCode(requiredOriginUnit(item));
      const requestedQuantity = Number(item?.quantity ?? 0);
      const requestedUnit = unitCode(item?.quantity_unit || baseUnit);
      const factor = Number(
        inventory?.price_conversion_factor_snapshot ||
          inventory?.conversion_factor_to_base ||
          conversionFactorForItem(item, inventoryUnit)
      );
      const inventoryUnitLabel =
        inventory?.price_unit_label ||
        inventory?.quantity_unit_label ||
        inventoryUnitInfo.label ||
        inventoryUnit;
      const usesPackaging =
        factor > 1 || inventoryUnit !== baseUnit || requestedUnit !== inventoryUnit;
      const availableBase = inventoryQuantity * factor;
      const sendingBase = Number(requiredOriginQuantity(item));
      const sendingPackages = factor > 0 ? sendingBase / factor : sendingBase;
      const remainingBase = availableBase - sendingBase;
      const remainingPackages = factor > 0 ? remainingBase / factor : remainingBase;
      const shortfallBase = Math.max(0, Math.abs(remainingBase));
      const shortfallPackages = factor > 0 ? shortfallBase / factor : shortfallBase;

      const available = usesPackaging
        ? `${formatQuantity(inventoryQuantity)} ${inventoryUnitLabel} = ${formatQuantity(availableBase)} ${baseUnit}`
        : `${formatQuantity(inventoryQuantity)} ${inventoryUnitLabel}`;
      const sending = usesPackaging
        ? `${formatQuantity(sendingBase)} ${baseUnit} from ${formatQuantity(sendingPackages)} ${inventoryUnitLabel}`
        : `${formatQuantity(sendingBase)} ${baseUnit}`;
      const remaining = usesPackaging
        ? `${formatQuantity(remainingBase)} ${baseUnit} (${formatQuantity(remainingPackages)} ${inventoryUnitLabel})`
        : `${formatQuantity(remainingBase)} ${baseUnit}`;

      return {
        requested: `${formatQuantity(requestedQuantity)} ${requestedUnit}`,
        available,
        sending,
        remaining,
        shortfall: usesPackaging
          ? `${formatQuantity(shortfallBase)} ${baseUnit} (${formatQuantity(shortfallPackages)} ${inventoryUnitLabel})`
          : `${formatQuantity(shortfallBase)} ${baseUnit}`,
        remainingBase,
        usesPackaging,
        formula: usesPackaging
          ? `${formatQuantity(inventoryQuantity)} x ${formatQuantity(factor)} ${baseUnit}/${inventoryUnitLabel} - ${formatQuantity(sendingBase)} ${baseUnit} = ${formatQuantity(remainingBase)} ${baseUnit}`
          : "",
      };
    };

    const quantityLabel = (item) => {
      const unit =
        item?.quantity_unit_label ||
        item?.unit_conversion?.source?.quantity_unit_label ||
        item?.quantity_unit ||
        item?.product?.quantity_unit ||
        "U";
      return `${item?.quantity ?? 0} ${unit}`;
    };

    const requiresExplicitConversion = (item) => {
      return !!item?.unit_conversion?.product_mapping_unit_conversion
        ?.requires_explicit_conversion;
    };

    const applyDefaultQuantityUnitOption = async (item) => {
      const option = item?.unit_conversion?.default_quantity_unit_update_option;
      if (!option?.endpoint || !option?.default_quantity_unit) return;

      applyingUnitOption.value = {
        ...applyingUnitOption.value,
        [item.id]: true,
      };
      try {
        const endpoint = option.endpoint.replace(/^\/api/, "");
        await http.patch(endpoint, {
          default_quantity_unit: option.default_quantity_unit,
        });
        toast.success("Quantity unit updated");
        await fetchDetails();
      } catch (error) {
        console.error("Failed to update product quantity unit", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Failed to update quantity unit"
        );
      } finally {
        applyingUnitOption.value = {
          ...applyingUnitOption.value,
          [item.id]: false,
        };
      }
    };

    const fetchDetails = async () => {
      try {
        console.log("🔄 Fetching transfer request details...");
        const { data } = await http.get(
          `/v2/groups/${props.groupId}/transfer-requests/${props.request.id}`
        );

        const newItems = Array.isArray(data?.items) ? data.items : [];
        console.log(
          "✅ Received items from API:",
          JSON.stringify(newItems, null, 2)
        );

        // Check if we need to fetch mappings separately
        const itemsNeedingMappingCheck = newItems.filter(
          (item) => item.product_id && !item.product_mapping_id
        );

        if (itemsNeedingMappingCheck.length > 0) {
          console.log(
            `🔍 Checking for existing mappings for ${itemsNeedingMappingCheck.length} items...`
          );

          const productIds = itemsNeedingMappingCheck.map(
            (item) => item.product_id
          );
          console.log("📦 Looking for mappings for products:", productIds);

          try {
            // Fetch only the specific mappings we need by passing product IDs to the API
            const mappingsResponse = await http.get(
              `/v2/store/${props.request.store_id}/product-mappings`,
              {
                params: {
                  other_store_id: props.request.other_store_id,
                  product_ids: productIds.join(","), // Send specific product IDs
                  is_active: true,
                  per_page: productIds.length, // Exact count needed
                },
              }
            );

            const existingMappings = Array.isArray(
              mappingsResponse?.data?.items
            )
              ? mappingsResponse.data.items
              : [];

            console.log(
              `📦 API returned ${existingMappings.length} mappings for ${productIds.length} products:`,
              existingMappings
            );

            // Create a map of product_id -> mapping_id
            const mappingMap = new Map();
            existingMappings.forEach((mapping) => {
              if (mapping.product?.id) {
                mappingMap.set(mapping.product.id, mapping.id);
              }
            });

            // Update items with mapping IDs if they exist
            newItems.forEach((item) => {
              if (!item.product_mapping_id && item.product_id) {
                const mappingId = mappingMap.get(item.product_id);
                if (mappingId) {
                  item.product_mapping_id = mappingId;
                  console.log(
                    `✅ Found existing mapping for item ${item.id} (product ${item.product_id}): mapping_id=${mappingId}`
                  );
                }
              }
            });
          } catch (mappingError) {
            console.warn(
              "⚠️ Failed to fetch mappings, continuing without them:",
              mappingError
            );
          }
        }

        // Preserve existing reactive state for items that are being updated
        const oldItemsMap = new Map();
        items.value.forEach((item) => {
          oldItemsMap.set(item.id, {
            creatingMapping: item.creatingMapping,
            targetSearchQuery: item.targetSearchQuery,
            targetProductResults: item.targetProductResults,
            searchingTargetProducts: item.searchingTargetProducts,
          });
        });

        // Update items with fresh data and preserve/add reactive properties
        items.value = newItems.map((item) => {
          const oldState = oldItemsMap.get(item.id);
          return {
            ...item,
            creatingMapping: oldState?.creatingMapping || false,
            targetSearchQuery: oldState?.targetSearchQuery || "",
            targetProductResults: oldState?.targetProductResults || [],
            searchingTargetProducts: oldState?.searchingTargetProducts || false,
          };
        });

        console.log(
          "✅ Final items with mapping status:",
          items.value.map((i) => ({
            id: i.id,
            product_mapping_id: i.product_mapping_id,
            product_id: i.product_id,
            product: i.product?.name,
            is_linked: !!i.product_mapping_id,
          }))
        );
      } catch (e) {
        console.error("❌ Failed to fetch request details", e);
      }

      // Fetch target product details and inventory
      await fetchTargetProductsAndInventory(items.value);
    };

    const fetchTargetProductsAndInventory = async (requestItems) => {
      if (!requestItems || requestItems.length === 0) return;

      // Get mapped items with target product IDs
      const mappedItems = requestItems.filter(
        (item) => item.product_mapping_id
      );

      if (mappedItems.length === 0) {
        console.log("📦 No mapped items yet, skipping target inventory fetch");
        return;
      }

      try {
        // Fetch full mapping details to get target product info
        console.log(
          "🔍 Fetching mapping details for",
          mappedItems.length,
          "items"
        );

        // Get product IDs from mapped items
        const productIds = mappedItems
          .map((item) => item.product_id)
          .filter((id) => id);

        // Fetch mappings to get other product details
        const mappingsResponse = await http.get(
          `/v2/store/${props.request.store_id}/product-mappings`,
          {
            params: {
              other_store_id: props.request.other_store_id,
              product_ids: productIds.join(","), // Only fetch needed mappings
              per_page: mappedItems.length,
            },
          }
        );

        const mappings = Array.isArray(mappingsResponse?.data?.items)
          ? mappingsResponse.data.items
          : [];

        console.log("📦 Received mappings:", mappings);

        // Update items with target product info from mappings
        const mappingMap = new Map();
        mappings.forEach((mapping) => {
          mappingMap.set(mapping.id, mapping.other_product);
        });

        requestItems.forEach((item) => {
          if (
            item.product_mapping_id &&
            mappingMap.has(item.product_mapping_id)
          ) {
            item.other_product = mappingMap.get(item.product_mapping_id);
          }
        });

        await Promise.all(
          requestItems
            .filter((item) => item.other_product?.id)
            .map((item) =>
              Promise.all([
                loadTargetQuantityUnits(item),
                loadPurchaseUnitPrice(item),
              ])
            )
        );

        const otherProductIds = mappings
          .map((m) => m.other_product?.id)
          .filter((id) => id);

        if (otherProductIds.length > 0) {
          console.log(
            "📦 Fetching OTHER STORE inventory (fulfiller) for products:",
            otherProductIds
          );
          const inventoryData = await Promise.all(
            [...new Set(otherProductIds)].map((productId) =>
              fetchInventoryProductDetails(props.request.other_store_id, productId)
            )
          );
          console.log("✅ Received other store inventory data:", inventoryData);

          // Create a map of product_id -> inventory info
          const newMap = new Map();
          inventoryData.filter(Boolean).forEach((inventoryItem) => {
            const product = inventoryItem?.product || {};
            const price = inventoryItem?.price || {};
            const productId = product.id;
            const unitInfo = unitInfoForProduct(
              productId,
              price.price_unit_code ||
                product.quantity_unit ||
                product.default_quantity_unit
            );
            newMap.set(productId, {
              product,
              quantity: Number(inventoryItem?.quantity || 0),
              quantity_unit:
                price.price_unit_code ||
                product.quantity_unit ||
                product.default_quantity_unit ||
                unitInfo.code,
              quantity_unit_label: price.price_unit_label || unitInfo.label,
              price_unit_code: price.price_unit_code || unitInfo.code,
              price_unit_label: price.price_unit_label || unitInfo.label,
              price_unit_scope: price.price_unit_scope || unitInfo.scope,
              price_base_quantity_unit:
                price.price_base_quantity_unit || unitInfo.baseUnit,
              price_conversion_factor_snapshot:
                price.price_conversion_factor_snapshot || unitInfo.factor,
              unit_price: purchaseUnitPrices.value[productId] ?? price.unit_price,
              raw: inventoryItem,
            });
          });

          targetInventoryMap.value = newMap;
          console.log(
            "✅ Target inventory map updated:",
            Object.fromEntries(newMap)
          );
        }
      } catch (error) {
        console.error("❌ Failed to fetch target products/inventory:", error);
        targetInventoryMap.value = new Map();
      }
    };

    const fetchInventoryProductDetails = async (storeId, productId) => {
      const response = await http.get(`/v2/store/inventory/${storeId}`, {
        params: {
          "id[eq]": productId,
          include: "has_rra_ebm_metadata",
          page: 1,
          per_page: 1,
        },
      });
      const inventoryItems = Array.isArray(response?.data?.items)
        ? response.data.items
        : [];
      return inventoryItems[0] || null;
    };

    const loadTargetQuantityUnits = async (item) => {
      const productId = item?.other_product?.id;
      if (!productId) return;
      try {
        const response = await http.get("/v1/store/inventory/quantity/units", {
          params: {
            store_id: props.request.other_store_id,
            product_id: productId,
          },
        });
        const units = Array.isArray(response?.data) ? response.data : [];
        targetQuantityUnitsMap.value = new Map(targetQuantityUnitsMap.value).set(
          productId,
          units.map((unit) => normalizeUnitRecord(unit, productId))
        );
      } catch (error) {
        console.warn("Failed to load quantity units for transfer product", error);
      }
    };

    const loadPurchaseUnitPrice = async (item) => {
      const productId = item?.other_product?.id;
      const productName = item?.other_product?.name || "";
      if (!productId) return;

      purchasePriceLoading.value = {
        ...purchasePriceLoading.value,
        [item.id]: true,
      };
      try {
        const response = await http.get(
          `/v2/store/${props.request.other_store_id}/purchases`,
          {
            params: {
              query: productName,
              per_page: 25,
              sort: "-created_at",
            },
          }
        );
        const purchases = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        const latestPurchase = purchases.find(
          (purchase) => String(purchase.product_id) === String(productId)
        );
        const price = Number(latestPurchase?.unit_price ?? 0);
        purchaseUnitPrices.value = {
          ...purchaseUnitPrices.value,
          [item.id]: Number.isFinite(price) ? price : 0,
          [productId]: Number.isFinite(price) ? price : 0,
        };
        purchasePriceHints.value = {
          ...purchasePriceHints.value,
          [item.id]: latestPurchase
            ? `Defaulted from ${props.storeName(props.request?.other_store_id)} current purchase`
            : `No current purchase price in ${props.storeName(props.request?.other_store_id)}`,
        };
      } catch (error) {
        console.warn("Failed to load purchase unit price", error);
        purchaseUnitPrices.value = {
          ...purchaseUnitPrices.value,
          [item.id]: Number(purchaseUnitPrices.value[item.id] || 0),
        };
        purchasePriceHints.value = {
          ...purchasePriceHints.value,
          [item.id]: `No current purchase price in ${props.storeName(props.request?.other_store_id)}`,
        };
      } finally {
        purchasePriceLoading.value = {
          ...purchasePriceLoading.value,
          [item.id]: false,
        };
      }
    };

    const startCreatingMapping = (item) => {
      item.creatingMapping = true;
      item.targetSearchQuery = "";
      item.targetProductResults = [];
    };

    const cancelCreatingMapping = (item) => {
      item.creatingMapping = false;
      item.targetSearchQuery = "";
      item.targetProductResults = [];
    };

    let searchTimeout = null;
    const searchTargetProducts = (item) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(async () => {
        const query = item.targetSearchQuery?.trim();
        if (!query || query.length < 2) {
          item.targetProductResults = [];
          return;
        }

        item.searchingTargetProducts = true;
        try {
          const response = await http.get(
            `/v2/store/product/store/search/${props.request.other_store_id}`,
            {
              params: {
                query: query,
                per_page: 10,
              },
            }
          );
          item.targetProductResults = Array.isArray(response?.data?.items)
            ? response.data.items
            : [];
        } catch (error) {
          console.error("Failed to search target products", error);
          item.targetProductResults = [];
        } finally {
          item.searchingTargetProducts = false;
        }
      }, 300);
    };

    const selectTargetProduct = (item, targetProduct) => {
      // Show confirmation dialog instead of directly linking
      pendingLink.value = {
        sourceItem: item,
        targetProduct: targetProduct,
      };
      showLinkConfirmation.value = true;
    };

    const cancelLinkConfirmation = () => {
      showLinkConfirmation.value = false;
      pendingLink.value = null;
    };

    const confirmLinkProducts = async () => {
      const item = pendingLink.value?.sourceItem;
      const targetProduct = pendingLink.value?.targetProduct;

      if (!item || !targetProduct) return;

      console.log("\n" + "=".repeat(80));
      console.log("🔗 CREATING PRODUCT MAPPING");
      console.log("=".repeat(80));
      console.log("📦 Transfer Request Details:");
      console.log("   - Request ID:", props.request.id);
      console.log("   - Requester Store ID:", props.request.store_id);
      console.log(
        "   - Requester Store Name:",
        props.storeName(props.request.store_id)
      );
      console.log("   - Fulfiller Store ID:", props.request.other_store_id);
      console.log(
        "   - Fulfiller Store Name:",
        props.storeName(props.request.other_store_id)
      );
      console.log("\n📦 Item Details:");
      console.log("   - Item ID:", item.id);
      console.log("   - Product ID:", item.product_id);
      console.log("   - Product Name:", item.product?.name);
      console.log("   - Product SKU:", item.product?.item_sku);
      console.log("\n📦 Selected Other Product:");
      console.log("   - Other Product ID:", targetProduct.id);
      console.log("   - Other Product Name:", targetProduct.name);
      console.log(
        "   - Other Product SKU:",
        targetProduct.item_sku || targetProduct.sku
      );
      console.log("\n🚨 VALIDATION:");
      console.log(
        "   - Same Product ID?",
        item.product_id === targetProduct.id
          ? "❌ YES - THIS IS WRONG!"
          : "✅ No"
      );

      try {
        // Create the mapping
        const payload = {
          other_store_id: props.request.other_store_id,
          other_product_id: targetProduct.id,
          product_id: item.product_id,
          mapping_confidence: "MANUAL",
        };

        console.log("\n📝 Mapping Payload:");
        console.log(
          "   - API URL: /v2/store/" +
            props.request.store_id +
            "/product-mappings"
        );
        console.log("   - Payload:", JSON.stringify(payload, null, 2));

        const response = await http.post(
          `/v2/store/${props.request.store_id}/product-mappings`,
          payload
        );

        const newMapping = response?.data;
        console.log("✅ Mapping created, full response:", newMapping);

        if (newMapping?.id) {
          // Update the item IMMEDIATELY with the new mapping ID from response
          item.product_mapping_id = newMapping.id;
          console.log(
            `✅ Updated item ${item.id} with mapping_id: ${newMapping.id}`
          );

          toast.success("Product link created successfully");

          // Close the confirmation modal
          showLinkConfirmation.value = false;
          pendingLink.value = null;

          // Close the search UI
          item.creatingMapping = false;
          item.targetSearchQuery = "";
          item.targetProductResults = [];

          // Force Vue to re-render by creating a new array reference
          items.value = [...items.value];

          // Fetch fresh data to get target product details and inventory
          await fetchDetails();
        }
      } catch (error) {
        console.error("❌ Failed to create product mapping:", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Failed to create product link"
        );
        showLinkConfirmation.value = false;
        pendingLink.value = null;
      }
    };

    const confirmRemoveLink = (item) => {
      itemToUnlink.value = item;
      showRemoveLinkConfirmation.value = true;
    };

    const confirmRemoveLinkAction = async () => {
      const item = itemToUnlink.value;
      if (!item || !item.product_mapping_id) return;

      const mappingIdToRemove = item.product_mapping_id;

      try {
        console.log(`🗑️ Removing product mapping: ${mappingIdToRemove}`);

        // Delete the mapping
        await http.delete(
          `/v2/store/${props.request.store_id}/product-mappings/${mappingIdToRemove}`
        );

        console.log("✅ Product link removed successfully");

        // Update the item IMMEDIATELY by removing the mapping ID
        item.product_mapping_id = null;
        console.log(`✅ Removed mapping from item ${item.id}`);

        toast.success("Product link removed successfully");

        // Close the confirmation modal
        showRemoveLinkConfirmation.value = false;
        itemToUnlink.value = null;

        // Force Vue to re-render
        items.value = [...items.value];

        // Refresh details in background to ensure consistency
        await fetchDetails();
      } catch (error) {
        console.error("❌ Failed to remove product mapping:", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Failed to remove product link"
        );
        showRemoveLinkConfirmation.value = false;
        itemToUnlink.value = null;
      }
    };

    const handleStart = () => {
      showStartConfirmation.value = false;
      emit("start", props.request, startNote.value?.trim() || null);
      startNote.value = "";
    };

    const buildCompleteNote = () => {
      const userNote = completeNote.value?.trim();
      const priceLines = (items.value || []).map((item) => {
        const price = Number(purchaseUnitPrices.value[item.id] ?? 0);
        const unit = item.quantity_unit || "unit";
        return `- ${item.product?.name || `Product #${item.product_id}`}: ${formatMoney(price)}/${unit}`;
      });
      const priceNote = priceLines.length
        ? `Purchase unit prices:\n${priceLines.join("\n")}`
        : "";
      return [userNote, priceNote].filter(Boolean).join("\n\n") || null;
    };

    const handleComplete = () => {
      if (!canCompleteWithPrices.value) return;
      showCompleteConfirmation.value = false;
      emit("complete", props.request, buildCompleteNote());
      completeNote.value = "";
    };

    const handleReject = () => {
      showRejectConfirmation.value = false;
      emit("reject", props.request, rejectNote.value?.trim() || null);
      rejectNote.value = "";
    };

    watch(
      () => props.isOpen,
      async (val) => {
        if (val && props.request?.id) {
          await fetchDetails();
        } else {
          items.value = [];
          targetInventoryMap.value = new Map();
          showStartConfirmation.value = false;
          showCompleteConfirmation.value = false;
          showRejectConfirmation.value = false;
          showLinkConfirmation.value = false;
          showRemoveLinkConfirmation.value = false;
          pendingLink.value = null;
          itemToUnlink.value = null;
          startNote.value = "";
          completeNote.value = "";
          rejectNote.value = "";
          purchaseUnitPrices.value = {};
          purchasePriceHints.value = {};
          purchasePriceLoading.value = {};
        }
      },
      { immediate: true }
    );

    return {
      items,
      targetInventoryMap,
      allItemsLinked,
      showStartConfirmation,
      showCompleteConfirmation,
      showRejectConfirmation,
      showLinkConfirmation,
      showRemoveLinkConfirmation,
      pendingLink,
      itemToUnlink,
      startNote,
      completeNote,
      rejectNote,
      applyingUnitOption,
      purchaseUnitPrices,
      purchasePriceHints,
      purchasePriceLoading,
      getStatusClass,
      formatDateTime,
      formatMoney,
      canCompleteWithPrices,
      requiredOriginQuantity,
      requiredOriginUnit,
      packagingTransferSummary,
      quantityLabel,
      requiresExplicitConversion,
      applyDefaultQuantityUnitOption,
      startCreatingMapping,
      cancelCreatingMapping,
      searchTargetProducts,
      selectTargetProduct,
      cancelLinkConfirmation,
      confirmLinkProducts,
      confirmRemoveLink,
      confirmRemoveLinkAction,
      handleStart,
      handleComplete,
      handleReject,
    };
  },
};
</script>

<style scoped>
:deep(.catalog-transfer-request-modal) {
  background:
    radial-gradient(circle at 92% 0%, rgba(16, 185, 129, 0.14), transparent 30%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 28px;
  box-shadow: 0 34px 100px rgba(15, 23, 42, 0.32);
  overflow: hidden;
}

:deep(.catalog-transfer-request-modal .modal-title) {
  color: var(--hs-text, #0f172a);
  font-size: 1.4rem;
  font-weight: 950;
}

:deep(.catalog-transfer-request-modal .close-btn) {
  align-items: center;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  border-radius: 14px;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;
  width: 40px;
}

:deep(.catalog-transfer-request-modal .close-btn:hover) {
  background: rgba(16, 185, 129, 0.12);
  transform: translateY(-1px);
}

.transfer-request-hero {
  background:
    linear-gradient(135deg, rgba(16, 185, 129, 0.1), transparent),
    color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 74%, var(--hs-surface, #ffffff));
}

.transfer-request-hero button {
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  font-weight: 900;
  min-height: 36px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.transfer-request-hero button:hover {
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
  transform: translateY(-1px);
}

.transfer-request-line-card {
  border-radius: 20px;
  border-color: var(--hs-border, rgba(148, 163, 184, 0.22));
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.transfer-request-line-card:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 26%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.11);
  transform: translateY(-2px);
}

@media (max-width: 760px) {
  .transfer-request-product-grid {
    grid-template-columns: 1fr;
  }
}
</style>
